import React, { useState } from "react";
import { 
    Card,
    Typography,
    Row,
    Col,
    Button,
    Form,
    Input,
    message,
    Space,
    Modal
} from 'antd';
import {
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    UserOutlined,
    BankOutlined,
    MailOutlined,
    CreditCardOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    RocketOutlined,
    ThunderboltOutlined,
    CrownOutlined,
    ShopOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { IOrganisation, IOrganisationSubscription, IBrand } from "../../services/nestjs-base-api/types";
import { TemplateTypeEnum } from '../../enums/TemplateTypeEnum';
import { useAuth } from '../../hooks/useAuth';
import {
    useUpdateOrganisationDetailsMutation,
    useGetOrganisationSubscriptionQuery,
    useGetOrganisationEntitlementsQuery,
    useGetOrganisationCreditsBalanceQuery,
    useGetQualificationConfigQuery
} from '../../services/nestjs-base-api';
import { businessModelLabel, productCategoryLabel } from '../../utils/qualificationLabels';
import { useScopedNavigate } from '../MarketerView/useScopedNavigate';

// Feature name mapping
const FEATURE_LABELS: Record<string, string> = {
    'UNRESTRICTED_ELEMENT_ACCESS': 'Full BE Model Access',
    'BENCHMARKING_OVERALL_ACCESS': 'Overall Benchmarking',
    'BENCHMARKING_PRODUCT_TYPE_ACCESS': 'Product Type Benchmarking',
    'BENCHMARKING_ALL_LEVELS_ACCESS': 'All Benchmarking Levels',
    'AI_EXPERTS_ACCESS': 'AI Experts',
    'AI_EXPERT_CREDITS_MONTHLY': 'Monthly AI Expert Credits',
    'AI_CREDITS_MONTHLY': 'Monthly AI Credits',
    'CUSTOMER_SEGMENTATION_FIELDS': 'Customer Segmentation',
};

// Features to hide (not implemented yet)
const HIDDEN_FEATURES = ['CUSTOMER_SEGMENTATION_FIELDS'];

const { Title, Text } = Typography;

interface IProps {
    organisation?: IOrganisation;
    bypassEnabled?: boolean;
    brands?: IBrand[];
}

const OrganisationDetails: React.FunctionComponent<IProps> = ({ organisation, bypassEnabled = false, brands = [] }) => {
    const { user } = useAuth();
    const navigate = useScopedNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();

    const [updateOrganisationDetails, { isLoading: isUpdating }] = useUpdateOrganisationDetailsMutation();

    // Fetch organisation subscription
    const { data: orgSubscription } = useGetOrganisationSubscriptionQuery({
        jwtToken: user!.jwtToken,
        orgId: String(user!.organisation.id),
    });

    // Fetch entitlements (plan features)
    const { data: entitlements } = useGetOrganisationEntitlementsQuery({
        jwtToken: user!.jwtToken,
        orgId: String(user!.organisation.id),
    });

    // Fetch AI credits balance
    const { data: credits } = useGetOrganisationCreditsBalanceQuery({
        jwtToken: user!.jwtToken,
        orgId: String(user!.organisation.id),
    });

    // Qualification config supplies human labels for the org's stored
    // business_model / product_category values.
    const { data: qualificationConfig } = useGetQualificationConfigQuery();

    // Helper to get feature label
    const getFeatureLabel = (code: string): string => {
        return FEATURE_LABELS[code] || code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get the primary brand's configuration (from quick start wizard)
    const primaryBrand = brands[0];
    const templateType = primaryBrand?.templateType as TemplateTypeEnum | null | undefined;

    // Helper to get business model label (inferred from template type)
    const getBusinessModelLabel = (type: TemplateTypeEnum | null | undefined): string | null => {
        if (!type) return null;
        if (type === TemplateTypeEnum.THREE_P_DURABLE) {
            return '3rd Party Retailer';
        }
        return 'Direct to Consumer (D2C)';
    };

    // Helper to get product type label
    const getProductTypeLabel = (type: TemplateTypeEnum | null | undefined): string | null => {
        if (!type) return null;
        switch (type) {
            case TemplateTypeEnum.DURABLE:
            case TemplateTypeEnum.THREE_P_DURABLE:
                return 'Durable Goods';
            case TemplateTypeEnum.PERISHABLE:
                return 'Perishable Goods';
            default:
                return null;
        }
    };

    // Prefer the org's stored business_model / product_category (labelled via
    // the qualification config); fall back to the templateType-derived labels
    // for legacy orgs whose columns were never populated.
    const displayBusinessModel =
        businessModelLabel(qualificationConfig, organisation?.businessModel) ??
        getBusinessModelLabel(templateType);
    const displayProductType =
        productCategoryLabel(qualificationConfig, organisation?.productCategory) ??
        getProductTypeLabel(templateType);

    // Filter visible entitlements (boolean features only for the checklist)
    const visibleFeatures = (entitlements || [])
        .filter(e => !HIDDEN_FEATURES.includes(e.feature) && e.type === 'boolean');

    // Return loading state if organisation is not yet available
    if (!organisation) {
        return (
            <div style={{ padding: '0 0 24px 0', textAlign: 'center' }}>
                <Title level={3}>Loading organization details...</Title>
            </div>
        );
    }

    // Helper function to format subscription display
    const formatSubscriptionInfo = (subscription: IOrganisationSubscription | null | undefined) => {
        if (!subscription) {
            return {
                planName: 'Free',
                status: 'inactive',
                statusDisplay: 'No active subscription',
                statusColor: '#d9d9d9',
                statusIcon: <WarningOutlined />,
                amount: 0,
                billingCycle: '',
                trialEnd: null,
                daysRemaining: 0,
                isTrialing: false,
                nextBillingDate: null,
                hasSubscription: false
            };
        }

        // Get plan name from the plan object or code
        const planName = subscription.plan?.name ||
            (subscription.plan?.code ? subscription.plan.code.charAt(0) + subscription.plan.code.slice(1).toLowerCase() : 'Unknown Plan');
        const status = subscription.status?.toLowerCase() || 'unknown';
        const amount = subscription.plan?.priceCents ? Math.round(subscription.plan.priceCents / 100) : 0;
        const billingCycle = subscription.billingCycle === 'annual' ? 'year' : 'month';

        // Status configuration
        const statusConfig: Record<string, { display: string; color: string; icon: React.ReactNode }> = {
            active: { display: 'Active', color: '#52c41a', icon: <CheckCircleOutlined /> },
            trialing: { display: 'Trial', color: '#fa8c16', icon: <ClockCircleOutlined /> },
            past_due: { display: 'Past Due', color: '#ff4d4f', icon: <WarningOutlined /> },
            canceled: { display: 'Canceled', color: '#d9d9d9', icon: <CloseOutlined /> },
            founding_cohort: { display: 'Founding', color: '#faad14', icon: <CrownOutlined /> },
            trial_expired: { display: 'Trial Expired', color: '#ff4d4f', icon: <WarningOutlined /> }
        };

        const currentStatus = statusConfig[status] || statusConfig.active;

        // Calculate trial info (trialing status uses currentPeriodEnd as trial end)
        let daysRemaining = 0;
        let trialEndDate = null;
        if (status === 'trialing' && subscription.currentPeriodEnd) {
            trialEndDate = new Date(subscription.currentPeriodEnd);
            daysRemaining = Math.ceil((trialEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            daysRemaining = Math.max(0, daysRemaining);
        }

        // Next billing date
        let nextBillingDate = null;
        if (subscription.currentPeriodEnd) {
            nextBillingDate = new Date(subscription.currentPeriodEnd);
        }

        return {
            planName,
            status,
            statusDisplay: currentStatus.display,
            statusColor: currentStatus.color,
            statusIcon: currentStatus.icon,
            amount,
            billingCycle,
            trialEnd: trialEndDate,
            daysRemaining,
            isTrialing: status === 'trialing',
            nextBillingDate,
            hasSubscription: true
        };
    };

    const subscriptionInfo = formatSubscriptionInfo(orgSubscription);
    
    // Plan icons and colors
    const planConfig: Record<string, { icon: React.ReactNode; color: string }> = {
        professional: {
            icon: <RocketOutlined />,
            color: '#1890ff',
        },
        enterprise: {
            icon: <CrownOutlined />,
            color: '#722ed1',
        },
        founding: {
            icon: <CrownOutlined />,
            color: '#faad14',
        },
        starter: {
            icon: <ThunderboltOutlined />,
            color: '#52c41a',
        },
        free: {
            icon: <ThunderboltOutlined />,
            color: '#8c8c8c',
        }
    };

    // Check if subscription is in an active state (not canceled)
    const activeStatuses = ['active', 'trialing', 'past_due', 'founding_cohort'];
    const isSubscriptionActive = activeStatuses.includes(subscriptionInfo.status);

    // Check if founding member (by status or plan code) - must also be active
    const isFoundingMember = subscriptionInfo.status === 'founding_cohort' ||
        (isSubscriptionActive && (orgSubscription?.plan?.code === 'FOUNDING' ||
        subscriptionInfo.planName.toLowerCase() === 'founding'));

    // Only show paid plan UI if subscription is active
    // Plan names are now "Professional", "Professional (Annual)", "Enterprise", etc.
    const isProfessional = isSubscriptionActive && subscriptionInfo.planName.toLowerCase().startsWith('professional');
    const isEnterprise = isSubscriptionActive && subscriptionInfo.planName.toLowerCase().startsWith('enterprise');
    const isPaidPlan = isProfessional || isEnterprise || isFoundingMember;

    // Check if subscription is canceling at period end (still has access but will end)
    const isCancelingAtPeriodEnd = orgSubscription?.cancelAtPeriodEnd &&
        subscriptionInfo.status !== 'canceled' &&
        (subscriptionInfo.planName.toLowerCase().startsWith('professional') ||
         subscriptionInfo.planName.toLowerCase().startsWith('enterprise'));

    // Calculate days until cancellation
    const daysUntilCancellation = isCancelingAtPeriodEnd && subscriptionInfo.nextBillingDate
        ? Math.max(0, Math.ceil((subscriptionInfo.nextBillingDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;

    const currentPlanConfig = planConfig[subscriptionInfo.planName.toLowerCase()] || planConfig.starter;

    const handleEdit = () => {
        form.setFieldsValue({
            name: organisation.name,
            billingEmail: organisation.billingEmail || '',
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        form.resetFields();
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            await updateOrganisationDetails({
                jwtToken: user!.jwtToken,
                payload: {
                    name: values.name,
                    billingEmail: values.billingEmail || undefined,
                },
            }).unwrap();

            message.success('Organisation details updated successfully');
            setIsEditing(false);
            
            // Refresh the page to show updated details
            window.location.reload();
        } catch (error: any) {
            if (error?.data?.message) {
                message.error(error.data.message);
            } else {
                message.error('Failed to update organisation details');
            }
        }
    };

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            {/* Header Section */}
            <div style={{ marginBottom: 32 }}>
                <Title level={3} style={{ margin: 0, marginBottom: 8 }}>Organization Overview</Title>
                <Text type="secondary">Manage your organization settings and subscription</Text>
            </div>

            {isEditing ? (
                <Modal
                    title="Edit Organization Details"
                    open={true}
                    onCancel={handleCancel}
                    footer={null}
                    width={600}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                    >
                        <Form.Item
                            label="Organization Name"
                            name="name"
                            rules={[
                                { required: true, message: 'Please enter organization name' },
                                { min: 2, message: 'Name must be at least 2 characters' },
                                { max: 100, message: 'Name must not exceed 100 characters' }
                            ]}
                        >
                            <Input 
                                prefix={<BankOutlined />}
                                placeholder="Enter organization name"
                                size="large"
                            />
                        </Form.Item>
                        
                        <Form.Item
                            label="Billing Email Address"
                            name="billingEmail"
                            rules={[
                                { type: 'email', message: 'Please enter a valid email address' }
                            ]}
                        >
                            <Input 
                                prefix={<MailOutlined />}
                                placeholder="billing@yourcompany.com"
                                size="large"
                            />
                        </Form.Item>
                        
                        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                            <Space>
                                <Button 
                                    type="primary" 
                                    icon={<SaveOutlined />}
                                    htmlType="submit"
                                    loading={isUpdating}
                                    size="large"
                                >
                                    Save Changes
                                </Button>
                                <Button 
                                    icon={<CloseOutlined />}
                                    onClick={handleCancel}
                                    size="large"
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            ) : (
                <Row gutter={[24, 24]}>
                    {/* Subscription Status Card - Only show if bypass is not enabled */}
                    {!bypassEnabled && (
                        <Col xs={24} lg={12}>
                        {/* Founding Member Card */}
                        {isFoundingMember ? (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Card
                                    style={{
                                        background: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)',
                                        border: '2px solid #faad14',
                                        borderRadius: 16,
                                    }}
                                >
                                    <Row align="middle" gutter={[24, 16]}>
                                        <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                                            <CrownOutlined style={{ fontSize: 48, color: '#faad14' }} />
                                        </Col>
                                        <Col xs={24} md={20}>
                                            <Title level={4} style={{ margin: 0, marginBottom: 8, color: '#ad6800' }}>
                                                <FormattedMessage id="subscription.founding.title" defaultMessage="Thank You, Founding Member!" />
                                            </Title>
                                            <Text style={{ color: '#614700' }}>
                                                <FormattedMessage id="subscription.founding.description" defaultMessage="As a founding member, you have unlimited access to all features including AI Experts, advanced benchmarking, and premium analytics. Your support helped build BrandScience.ai!" />
                                            </Text>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* AI Expert Credits Card for Founding - Unlimited */}
                                <Card
                                    title={
                                        <Space>
                                            <ThunderboltOutlined style={{ color: '#faad14' }} />
                                            <span><FormattedMessage id="subscription.credits.title" defaultMessage="AI Expert Credits" /></span>
                                        </Space>
                                    }
                                >
                                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                        <Text strong style={{ fontSize: 32, color: '#faad14' }}>
                                            <FormattedMessage id="subscription.credits.unlimited" defaultMessage="Unlimited" />
                                        </Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary">
                                                <FormattedMessage id="subscription.credits.unlimitedDescription" defaultMessage="As a Founding member, you have unlimited AI Expert credits" />
                                            </Text>
                                        </div>
                                    </div>
                                </Card>

                                {/* Plan Features Card for Founding */}
                                {visibleFeatures.length > 0 && (
                                    <Card title={<FormattedMessage id="subscription.planFeatures" defaultMessage="Plan Features" />}>
                                        <Row gutter={[8, 8]}>
                                            {visibleFeatures.map(feature => (
                                                <Col key={feature.feature} span={24}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px 12px',
                                                        background: feature.hasAccess ? '#f6ffed' : '#fafafa',
                                                        border: `1px solid ${feature.hasAccess ? '#b7eb8f' : '#d9d9d9'}`,
                                                        borderRadius: 6,
                                                    }}>
                                                        {feature.hasAccess ? (
                                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                                        ) : (
                                                            <CloseOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
                                                        )}
                                                        <Text style={{ color: feature.hasAccess ? '#262626' : '#8c8c8c' }}>
                                                            {getFeatureLabel(feature.feature)}
                                                        </Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Card>
                                )}
                            </Space>
                        ) : isCancelingAtPeriodEnd ? (
                            /* Cancellation Pending Card */
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Card
                                    style={{
                                        background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
                                        border: '2px solid #fa8c16',
                                        borderRadius: 16,
                                    }}
                                >
                                    <Row align="middle" gutter={[24, 16]}>
                                        <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                                            <CrownOutlined style={{ fontSize: 48, color: '#fa8c16' }} />
                                        </Col>
                                        <Col xs={24} md={14}>
                                            <Title level={4} style={{ margin: 0, marginBottom: 8, color: '#d46b08' }}>
                                                {subscriptionInfo.planName} Plan - Canceling
                                            </Title>
                                            <div style={{
                                                background: '#fffbe6',
                                                border: '1px solid #ffe58f',
                                                borderRadius: 6,
                                                padding: '8px 12px',
                                            }}>
                                                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                                <Text style={{ color: '#614700' }}>
                                                    Your subscription will end on {subscriptionInfo.nextBillingDate?.toLocaleDateString()}. You'll retain access until then.
                                                </Text>
                                            </div>
                                        </Col>
                                        <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                                            <div style={{
                                                background: 'rgba(255,255,255,0.7)',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                            }}>
                                                <Text style={{ display: 'block', color: '#d46b08', fontSize: 12 }}>
                                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                    Ends in
                                                </Text>
                                                <Text strong style={{ fontSize: 20, color: '#d46b08' }}>
                                                    {daysUntilCancellation}
                                                </Text>
                                                <Text style={{ display: 'block', color: '#d46b08', fontSize: 12 }}>
                                                    days
                                                </Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Plan Features Card - still show what they have access to */}
                                {visibleFeatures.length > 0 && (
                                    <Card title={<FormattedMessage id="subscription.planFeatures" defaultMessage="Plan Features" />}>
                                        <Row gutter={[8, 8]}>
                                            {visibleFeatures.map(feature => (
                                                <Col key={feature.feature} span={24}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px 12px',
                                                        background: feature.hasAccess ? '#f6ffed' : '#fafafa',
                                                        border: `1px solid ${feature.hasAccess ? '#b7eb8f' : '#d9d9d9'}`,
                                                        borderRadius: 6,
                                                    }}>
                                                        {feature.hasAccess ? (
                                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                                        ) : (
                                                            <CloseOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
                                                        )}
                                                        <Text style={{ color: feature.hasAccess ? '#262626' : '#8c8c8c' }}>
                                                            {getFeatureLabel(feature.feature)}
                                                        </Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Card>
                                )}
                            </Space>
                        ) : isPaidPlan ? (
                            /* Paid Plan Cards - Professional or Enterprise */
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {/* Plan Banner - Professional (blue) or Enterprise (purple) */}
                                <Card
                                    style={{
                                        background: subscriptionInfo.planName.toLowerCase() === 'enterprise'
                                            ? 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)'
                                            : 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                                        border: subscriptionInfo.planName.toLowerCase() === 'enterprise'
                                            ? '2px solid #9333ea'
                                            : '2px solid #1890ff',
                                        borderRadius: 16,
                                    }}
                                >
                                    <Row align="middle" gutter={[24, 16]}>
                                        <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                                            <CrownOutlined style={{
                                                fontSize: 48,
                                                color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#9333ea' : '#1890ff'
                                            }} />
                                        </Col>
                                        <Col xs={24} md={14}>
                                            <Title level={4} style={{
                                                margin: 0,
                                                marginBottom: 8,
                                                color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#581c87' : '#0050b3'
                                            }}>
                                                <FormattedMessage
                                                    id="subscription.planActive"
                                                    defaultMessage="{planName} Plan Active"
                                                    values={{ planName: subscriptionInfo.planName }}
                                                />
                                            </Title>
                                            <Text style={{
                                                color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#6b21a8' : '#003a8c'
                                            }}>
                                                {subscriptionInfo.planName.toLowerCase() === 'enterprise'
                                                    ? <FormattedMessage id="subscription.enterprise.description" defaultMessage="You have full access to all Enterprise features including unlimited brands, all benchmarking levels, custom integrations, and dedicated support." />
                                                    : <FormattedMessage id="subscription.professional.description" defaultMessage="You have full access to all Professional features including advanced analytics and product type benchmarking." />}
                                            </Text>
                                        </Col>
                                        {subscriptionInfo.nextBillingDate && (
                                            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                                                <div style={{
                                                    background: 'rgba(255,255,255,0.7)',
                                                    borderRadius: 12,
                                                    padding: '12px 16px',
                                                }}>
                                                    <Text style={{
                                                        display: 'block',
                                                        color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#581c87' : '#0050b3',
                                                        fontSize: 12
                                                    }}>
                                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                        <FormattedMessage id="subscription.renewal" defaultMessage="Renewal" />
                                                    </Text>
                                                    <Text strong style={{
                                                        fontSize: 20,
                                                        color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#581c87' : '#0050b3'
                                                    }}>
                                                        {Math.ceil((subscriptionInfo.nextBillingDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))}
                                                    </Text>
                                                    <Text style={{
                                                        display: 'block',
                                                        color: subscriptionInfo.planName.toLowerCase() === 'enterprise' ? '#581c87' : '#0050b3',
                                                        fontSize: 12
                                                    }}>
                                                        <FormattedMessage id="subscription.days" defaultMessage="days" />
                                                    </Text>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </Card>

                                {/* AI Expert Credits Card */}
                                {credits && (() => {
                                    // Calculate available credits using entitlements (includes addon credits)
                                    const aiCreditsEntitlement = (entitlements || []).find(e => e.feature === 'AI_EXPERT_CREDITS_MONTHLY');
                                    const totalLimit = aiCreditsEntitlement?.limit || credits.totalCredits || 0;
                                    const used = aiCreditsEntitlement?.used || 0;
                                    const availableCredits = Math.max(0, totalLimit - used);

                                    return (
                                    <Card
                                        title={
                                            <Space>
                                                <ThunderboltOutlined style={{ color: '#F59E0B' }} />
                                                <span><FormattedMessage id="subscription.credits.title" defaultMessage="AI Expert Credits" /></span>
                                            </Space>
                                        }
                                        extra={
                                            <Button
                                                type="link"
                                                onClick={() => navigate('/dashboard/organisation?tab=billing&subtab=addons')}
                                            >
                                                <FormattedMessage id="subscription.credits.buyMore" defaultMessage="Buy More" />
                                            </Button>
                                        }
                                    >
                                        <Row gutter={[16, 16]}>
                                            <Col span={12}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text type="secondary"><FormattedMessage id="subscription.credits.available" defaultMessage="Available" /></Text>
                                                </div>
                                                <Text strong style={{ fontSize: 28, color: '#F59E0B' }}>
                                                    {availableCredits.toLocaleString()}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text type="secondary"><FormattedMessage id="subscription.credits.breakdown" defaultMessage="Breakdown" /></Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Text><FormattedMessage id="subscription.credits.planIncluded" defaultMessage="Plan included" /></Text>
                                                        <Text strong>{credits.planIncluded.toLocaleString()}</Text>
                                                    </div>
                                                    {credits.bonus > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text><FormattedMessage id="subscription.credits.bonus" defaultMessage="Bonus" /></Text>
                                                            <Text strong style={{ color: '#52c41a' }}>{credits.bonus.toLocaleString()}</Text>
                                                        </div>
                                                    )}
                                                    {/* Show addon credits from entitlements */}
                                                    {totalLimit > credits.planIncluded && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text><FormattedMessage id="subscription.credits.purchased" defaultMessage="Purchased" /></Text>
                                                            <Text strong>{(totalLimit - credits.planIncluded).toLocaleString()}</Text>
                                                        </div>
                                                    )}
                                                    {credits.reward > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text><FormattedMessage id="subscription.credits.rewards" defaultMessage="Rewards" /></Text>
                                                            <Text strong style={{ color: '#faad14' }}>{credits.reward.toLocaleString()}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card>
                                    );
                                })()}

                                {/* Plan Features Card */}
                                {visibleFeatures.length > 0 && (
                                    <Card title={<FormattedMessage id="subscription.planFeatures" defaultMessage="Plan Features" />}>
                                        <Row gutter={[8, 8]}>
                                            {visibleFeatures.map(feature => (
                                                <Col key={feature.feature} span={24}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px 12px',
                                                        background: feature.hasAccess ? '#f6ffed' : '#fafafa',
                                                        border: `1px solid ${feature.hasAccess ? '#b7eb8f' : '#d9d9d9'}`,
                                                        borderRadius: 6,
                                                    }}>
                                                        {feature.hasAccess ? (
                                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                                        ) : (
                                                            <CloseOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
                                                        )}
                                                        <Text style={{ color: feature.hasAccess ? '#262626' : '#8c8c8c' }}>
                                                            {getFeatureLabel(feature.feature)}
                                                        </Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Card>
                                )}
                            </Space>
                        ) : (
                            /* Free Plan Card - Same style as Professional */
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {/* Free Plan Banner */}
                                <Card
                                    style={{
                                        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                                        border: '2px solid #d9d9d9',
                                        borderRadius: 16,
                                    }}
                                >
                                    <Row align="middle" gutter={[24, 16]}>
                                        <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                                            <ThunderboltOutlined style={{ fontSize: 48, color: '#8c8c8c' }} />
                                        </Col>
                                        <Col xs={24} md={14}>
                                            <Title level={4} style={{ margin: 0, marginBottom: 8, color: '#262626' }}>
                                                <FormattedMessage id="subscription.free.title" defaultMessage="Free Plan" />
                                            </Title>
                                            <Text style={{ color: '#595959' }}>
                                                <FormattedMessage id="subscription.free.description" defaultMessage="You're on the Free plan with limited features. Upgrade to Professional to unlock advanced analytics, benchmarking, and AI Expert credits." />
                                            </Text>
                                        </Col>
                                        <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<RocketOutlined />}
                                                onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                                                style={{
                                                    background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                                                    border: 'none',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Upgrade
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Plan Features Card for Free users */}
                                {visibleFeatures.length > 0 && (
                                    <Card title={<FormattedMessage id="subscription.planFeatures" defaultMessage="Plan Features" />}>
                                        <Row gutter={[8, 8]}>
                                            {visibleFeatures.map(feature => (
                                                <Col key={feature.feature} span={24}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px 12px',
                                                        background: feature.hasAccess ? '#f6ffed' : '#fafafa',
                                                        border: `1px solid ${feature.hasAccess ? '#b7eb8f' : '#d9d9d9'}`,
                                                        borderRadius: 6,
                                                    }}>
                                                        {feature.hasAccess ? (
                                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                                        ) : (
                                                            <CloseOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
                                                        )}
                                                        <Text style={{ color: feature.hasAccess ? '#262626' : '#8c8c8c' }}>
                                                            {getFeatureLabel(feature.feature)}
                                                        </Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Card>
                                )}
                            </Space>
                        )}
                    </Col>
                    )}
                    
                    {/* Organization Details Card - Make full width when bypass is enabled */}
                    <Col xs={24} lg={bypassEnabled ? 24 : 12}>
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            {/* Basic Information Card */}
                            <Card 
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Organization Information</span>
                                        <Button 
                                            type="text" 
                                            icon={<EditOutlined />}
                                            onClick={handleEdit}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                }
                            >
                                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>ORGANIZATION NAME</Text>
                                        <div style={{ marginTop: 4 }}>
                                            <Space>
                                                <BankOutlined style={{ color: '#8c8c8c' }} />
                                                <Text strong style={{ fontSize: 16 }}>{organisation.name}</Text>
                                            </Space>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>BILLING EMAIL</Text>
                                        <div style={{ marginTop: 4 }}>
                                            <Space>
                                                <MailOutlined style={{ color: '#8c8c8c' }} />
                                                {organisation.billingEmail ? (
                                                    <a href={`mailto:${organisation.billingEmail}`} style={{ fontSize: 16 }}>
                                                        {organisation.billingEmail}
                                                    </a>
                                                ) : (
                                                    <Text type="secondary" style={{ fontSize: 16 }}>Not configured</Text>
                                                )}
                                            </Space>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>ORGANIZATION ID</Text>
                                        <div style={{ marginTop: 4 }}>
                                            <Text style={{ fontSize: 14, fontFamily: 'monospace' }}>{organisation.hash}</Text>
                                        </div>
                                    </div>
                                </Space>
                            </Card>

                            {/* Account Configuration Card */}
                            {(displayBusinessModel || displayProductType) && (
                                <Card title="Account Configuration">
                                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                        {displayBusinessModel && (
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>BUSINESS MODEL</Text>
                                                <div style={{ marginTop: 4 }}>
                                                    <Space>
                                                        <ShopOutlined style={{ color: '#8c8c8c' }} />
                                                        <Text strong style={{ fontSize: 16 }}>{displayBusinessModel}</Text>
                                                    </Space>
                                                </div>
                                            </div>
                                        )}

                                        {displayProductType && (
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>PRODUCT TYPE</Text>
                                                <div style={{ marginTop: 4 }}>
                                                    <Space>
                                                        <AppstoreOutlined style={{ color: '#8c8c8c' }} />
                                                        <Text strong style={{ fontSize: 16 }}>{displayProductType}</Text>
                                                    </Space>
                                                </div>
                                            </div>
                                        )}
                                    </Space>
                                </Card>
                            )}

                            {/* Quick Actions Card */}
                            <Card title="Quick Actions">
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {!bypassEnabled && subscriptionInfo.isTrialing && (
                                        <Button
                                            type="primary"
                                            icon={<CreditCardOutlined />}
                                            block
                                            size="large"
                                            style={{ background: currentPlanConfig.color, borderColor: currentPlanConfig.color }}
                                            onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                                        >
                                            Upgrade to {subscriptionInfo.planName}
                                        </Button>
                                    )}
                                    {!bypassEnabled && (
                                        <Button 
                                            icon={<CreditCardOutlined />}
                                            block
                                            onClick={() => navigate('/dashboard/organisation?tab=billing')}
                                        >
                                            Manage Billing & Subscriptions
                                        </Button>
                                    )}
                                    <Button 
                                        icon={<UserOutlined />}
                                        block
                                        onClick={() => navigate('/dashboard/organisation?tab=users')}
                                    >
                                        Manage Users
                                    </Button>
                                </Space>
                            </Card>
                        </Space>
                    </Col>
                </Row>
            )}
        </div>
    );
}

export default OrganisationDetails;
